import { useEffect, useMemo, useState } from "react";
import { HeadContent, Outlet, Scripts, createRootRoute } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/tanstack-react-start";
import { I18nextProvider, useTranslation } from "react-i18next";
import { ThemeProvider } from "next-themes";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { logError } from "@/lib/structured-log";
import { extractApiErrorMessage } from "@/lib/api/error";
import { getClerkPublishableKeyServer } from "@/server/clerk";
import type { ClerkPublishableKeyPayload } from "@/server/clerk";
import { getAnalyticsConfigServer } from "@/server/analytics";
import type { AnalyticsConfigPayload } from "@/server/analytics";
import { getSentryConfigServer } from "@/server/sentry";
import type { SentryConfigPayload } from "@/server/sentry";
import i18n from "@/i18n";
import { initSentry } from "@/lib/sentry";
import * as Sentry from "@sentry/react";

import appCss from "@/styles/app.css?url";

const DEFAULT_META_TITLE = "Workus AI | AI Sales and Sourcing Agents";
const DEFAULT_META_DESCRIPTION =
  "Workus AI helps B2B teams find suppliers, discover target accounts, automate outreach, and manage sales or sourcing workflows with AI agents.";
const DEFAULT_META_KEYWORDS =
  "Workus AI, AI sales agent, AI sourcing agent, B2B lead generation, supplier sourcing, sales automation, outreach automation, procurement automation";

export const Route = createRootRoute({
  // Cache Clerk and analytics config for 5 minutes (very static data)
  // This config rarely changes during a session
  staleTime: 5 * 60_000, // 5 minutes
  gcTime: 10 * 60_000, // 10 minutes until garbage collection
  loader: async (): Promise<
    ClerkPublishableKeyPayload & AnalyticsConfigPayload & SentryConfigPayload
  > => {
    const [clerkConfig, analyticsConfig, sentryConfig] = await Promise.all([
      getClerkPublishableKeyServer(),
      getAnalyticsConfigServer(),
      getSentryConfigServer(),
    ]);
    return { ...clerkConfig, ...analyticsConfig, ...sentryConfig };
  },
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: DEFAULT_META_TITLE,
      },
      {
        name: "description",
        content: DEFAULT_META_DESCRIPTION,
      },
      {
        name: "keywords",
        content: DEFAULT_META_KEYWORDS,
      },
      {
        property: "og:title",
        content: DEFAULT_META_TITLE,
      },
      {
        property: "og:description",
        content: DEFAULT_META_DESCRIPTION,
      },
      {
        property: "og:type",
        content: "website",
      },
      {
        name: "twitter:card",
        content: "summary",
      },
      {
        name: "twitter:title",
        content: DEFAULT_META_TITLE,
      },
      {
        name: "twitter:description",
        content: DEFAULT_META_DESCRIPTION,
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),

  // Note: pendingComponent is configured globally in router.tsx
  // using defaultPendingComponent and defaultPendingMs

  errorComponent: RootErrorComponent,

  notFoundComponent: NotFoundComponent,

  component: RootApp,
  shellComponent: RootDocument,
});

const ROOT_ERROR_RETRY_LIMIT = 1;
const rootBoundaryRetryAttempts = new Map<string, number>();
const rootBoundaryLastToastAt = new Map<string, number>();
const rootBoundaryRedirectAttempts = new Map<string, number>();

type RootError = ErrorComponentProps["error"];

function getRootErrorProperty(error: RootError, key: string): unknown {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  return (error as unknown as Record<string, unknown>)[key];
}

function getRootErrorName(error: RootError): string {
  const value = getRootErrorProperty(error, "name");
  return typeof value === "string" && value.trim().length > 0 ? value : "Error";
}

function getRootErrorMessage(error: RootError): string {
  const value = getRootErrorProperty(error, "message");
  return typeof value === "string" && value.trim().length > 0 ? value : "Unknown error";
}

function getRootErrorStack(error: RootError): string | undefined {
  const value = getRootErrorProperty(error, "stack");
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function buildRootErrorSignature(error: RootError): string {
  const firstStackLine = getRootErrorStack(error)?.split("\n")[0] ?? "";
  return `${getRootErrorName(error)}|${getRootErrorMessage(error)}|${firstStackLine}`;
}

function extractRootErrorDetail(error: RootError, fallbackMessage: string): string {
  return extractApiErrorMessage(error, fallbackMessage).trim();
}

function NotFoundComponent() {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">{t("common:errors.404")}</h1>
        <p className="text-gray-600 mb-8">{t("common:errors.pageNotFound")}</p>
        <a href="/" className="text-blue-600 hover:text-blue-800 underline">
          {t("common:errors.returnHome")}
        </a>
      </div>
    </div>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation();
  const title = t("common:errors.unexpectedError");
  const fallbackDescription = t("common:errors.errorDescription");
  const errorSignature = useMemo(() => buildRootErrorSignature(error), [error]);
  const errorName = useMemo(() => getRootErrorName(error), [error]);
  const errorMessage = useMemo(() => getRootErrorMessage(error), [error]);
  const errorStack = useMemo(() => getRootErrorStack(error), [error]);
  const detailMessage = useMemo(
    () => extractRootErrorDetail(error, fallbackDescription),
    [error, fallbackDescription],
  );
  const description =
    detailMessage.length > 0 && detailMessage !== title ? detailMessage : fallbackDescription;

  // Log and report the error once it hits the root boundary.
  useEffect(() => {
    logError("route.error.root_boundary", {
      message: errorMessage,
      name: errorName,
      stack: errorStack,
      signature: errorSignature,
    });

    Sentry.captureException(error, {
      tags: {
        errorBoundary: "root",
      },
      level: "error",
    });
  }, [error, errorMessage, errorName, errorSignature, errorStack]);

  // Surface details with toast and avoid duplicate floods on repeated remounts.
  useEffect(() => {
    const now = Date.now();
    const previousAt = rootBoundaryLastToastAt.get(errorSignature) ?? 0;
    if (now - previousAt > 1500) {
      toast.error(title, {
        description,
        duration: 12000,
      });
      rootBoundaryLastToastAt.set(errorSignature, now);
    }
  }, [description, errorSignature, title]);

  // Try one automatic recovery so users stay on the current page whenever possible.
  useEffect(() => {
    const attempts = rootBoundaryRetryAttempts.get(errorSignature) ?? 0;
    if (attempts < ROOT_ERROR_RETRY_LIMIT) {
      rootBoundaryRetryAttempts.set(errorSignature, attempts + 1);
      const timer = window.setTimeout(() => {
        reset();
      }, 0);
      return () => {
        window.clearTimeout(timer);
      };
    }

    const redirectAttempts = rootBoundaryRedirectAttempts.get(errorSignature) ?? 0;
    if (redirectAttempts < 1) {
      rootBoundaryRedirectAttempts.set(errorSignature, redirectAttempts + 1);
      const fallbackPath = (() => {
        const matched = window.location.pathname.match(/^\/workspace\/([^/]+)/);
        if (matched?.[1]) {
          return `/workspace/${matched[1]}/new-task${window.location.search}`;
        }
        return "/";
      })();
      const timer = window.setTimeout(() => {
        window.location.replace(fallbackPath);
      }, 0);
      return () => {
        window.clearTimeout(timer);
      };
    }
    return undefined;
  }, [errorSignature, reset]);

  return null;
}

function RootApp() {
  const { publishableKey, sentryDSN, sentryEnvironment } = Route.useLoaderData();

  // Initialize Sentry as early as possible
  // Config comes from server-side env vars, allowing runtime configuration
  useEffect(() => {
    initSentry(sentryDSN, sentryEnvironment);
  }, [sentryDSN, sentryEnvironment]);

  // Capture referral code from URL
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const searchParams = new URLSearchParams(window.location.search);
      const referralCode =
        searchParams.get("via") || searchParams.get("ref") || searchParams.get("referral_code");

      if (referralCode) {
        localStorage.setItem("referral_code", referralCode);
      }
    } catch {
      // Ignore URL parsing errors
    }
  }, []);

  // Create QueryClient instance for TanStack Query
  // Using useMemo to prevent recreating on every render
  const queryClient = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0, // Data considered stale immediately (for real-time kanban)
            gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes after unmount
            retry: 1, // Retry failed requests once
            refetchOnWindowFocus: false, // Don't refetch on window focus
          },
        },
      }),
  )[0];

  return (
    <ClerkProvider publishableKey={publishableKey}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
            <Outlet />
          </ThemeProvider>
          <Toaster />
        </I18nextProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const { gtmId } = Route.useLoaderData();

  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/* Google Tag Manager */}
        {gtmId && (
          <script
            id="gtm-head"
            dangerouslySetInnerHTML={{
              __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${gtmId}');
            `,
            }}
          />
        )}
      </head>
      <body>
        {/* Google Tag Manager (noscript fallback) */}
        {gtmId && (
          <noscript>
            <iframe
              title="Google Tag Manager"
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            />
          </noscript>
        )}
        {children}
        <Scripts />
      </body>
    </html>
  );
}
